import { useEffect, useState } from 'react'
import { useWindowDimensions } from 'react-native'

import { i18n } from '@localization'
import { useQueryClient } from '@tanstack/react-query'
import { publishEvent } from '@zet/analytics'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { Select } from '@zet/design-system/select'
import { B2, B5, SH3 } from '@zet/design-system/typography'
import { changeLocaleInHeader } from '@zet/network'
import RegistrationForm from '@zet/svg/registrationForm'
import { ScrollView } from 'react-native-gesture-handler'
import { ModalComponentProp } from 'react-native-modalfy'

import { ModalStackParams } from '../modalConfig'

type Props = ModalComponentProp<ModalStackParams, void, 'WebFlowSelectorModal'>

const WebFlowSelectorModal = ({ modal: { closeModal, getParam } }: Props) => {
  const [changedLocale, setChangedLocale] = useState('')
  const [selectedValue, setSelectedValue] = useState(
    i18n.locale === 'en' ? 'English' : 'Hindi'
  )
  const queryClient = useQueryClient()
  const store = useAddCustomerStore()
  const { width } = useWindowDimensions()

  const title = getParam('title')
  const iconName = getParam('iconName')
  const criteriaName = getParam('criteriaName')
  const data = getParam('options', [])
  const setValue = getParam('setValue')
  const type = getParam('type')
  const link = getParam('link')
  const event = getParam('event')
  const description = getParam('description')
  const selectName = getParam('selectName') || 'displayValue'

  function onChangeSelect(value: string) {
    setSelectedValue(value)
    if (criteriaName !== 'languageModal') {
      setValue(criteriaName, value, {
        shouldValidate: true,
        shouldTouched: true,
      })
      if (event?.eventName) {
        publishEvent(event.eventName, event?.eventPayload)
      }
      if (criteriaName !== 'bankName') {
        closeModal()
      }
    } else if (criteriaName === 'languageModal') {
      publishEvent('click_action', {
        item_name: 'add_customer_change_language',
        language: value,
      })
      if (value === 'English') {
        setChangedLocale('en')
      } else {
        setChangedLocale('hi')
      }
    }
  }

  useEffect(() => {
    publishEvent('screen_view', { screen_name: 'WebFlowSelectorModal' })
  }, [])
  return (
    <Stack>
      <Stack
        className="rounded bg-white pb-3"
        style={{ width: width > 640 ? 615 : width - 32 }}
      >
        <XStack className="border-grays-30 border-b-2 px-3 py-2">
          {criteriaName !== 'languageModal' && <Icon type={`${iconName}`} />}
          <SH3 className="mx-2 flex-grow" numberOfLines={1}>
            {title}
          </SH3>
          {criteriaName !== 'languageModal' && (
            <Pressable
              onPress={() => {
                if (type === 'leadAlready') {
                  closeModal()
                } else {
                  closeModal()
                }
              }}
            >
              <Icon type="Close" />
            </Pressable>
          )}
        </XStack>
        <ScrollView contentContainerStyle={{ maxHeight: 300 }}>
          <Stack className="px-3">
            <>
              {criteriaName === 'bankName' && (
                <B5 className="text-grays-60 mt-3">
                  You can select multiple banks
                </B5>
              )}
              <Select
                onChange={onChangeSelect}
                mode={criteriaName === 'bankName' ? 'multi' : 'single'}
              >
                {data.map((d) => {
                  if (criteriaName === d?.criteriaName) {
                    return d?.fields.map((field) => {
                      return (
                        <Select.Option
                          key={field.key}
                          className="my-2 w-full"
                          selectedValue={selectedValue}
                          optionContainerStyle="h-14 w-full my-1 justify-center rounded-lg"
                          optionTextStyle="pl-3 text-base font-[Graphie-SemiBold]"
                          name={field[selectName]}
                          showToggle={
                            criteriaName === 'bankName' ? true : false
                          }
                          iconColor={{
                            active: zetColors.primary[50],
                            inActive: zetColors.grays[60],
                          }}
                        >
                          {field.displayValue}
                        </Select.Option>
                      )
                    })
                  } else if (criteriaName === 'languageModal') {
                    return (
                      <Select.Option
                        key={d}
                        className="my-2 w-full"
                        selectedValue={selectedValue}
                        optionContainerStyle="h-14 w-full my-1 justify-center rounded-lg"
                        optionTextStyle="pl-3 text-base font-[Graphie-SemiBold]"
                        name={d}
                        showToggle={true}
                      >
                        {d}
                      </Select.Option>
                    )
                  }
                })}
              </Select>
              {criteriaName === 'applyModal' && (
                <>
                  <Stack className="mb-3 mt-3 self-center">
                    <RegistrationForm />
                  </Stack>
                  <B2 className="mx-3 mb-3 text-center">
                    {type === 'missingDetails'
                      ? i18n.t('shareCardLink.missingDetails')
                      : type === 'leadAlready'
                      ? i18n.t('shareCardLink.alreadyApplied')
                      : description}
                  </B2>
                  <Stack className="border-grays-30 border-t px-3 py-3">
                    {type === 'missingDetails' && (
                      <Button
                        className="w-full"
                        size="lg"
                        onPress={() => {
                          window.open(link)
                          publishEvent('click_action', {
                            item_name: 'apply_bottom_sheet_missing_details',
                          })
                        }}
                      >
                        {i18n.t('shareCardLink.fill')}
                      </Button>
                    )}
                    {type === 'notEligible' ||
                    type === 'leadAlready' ||
                    type === 'generic' ? (
                      <Button
                        className="w-full"
                        size="lg"
                        onPress={() => {
                          if (type === 'leadAlready') {
                            publishEvent('click_action', {
                              item_name:
                                'apply_bottom_sheet_lead_already_added_okay',
                            })
                          }
                          closeModal()
                        }}
                      >
                        {i18n.t('shareCardLink.okay')}
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Stack>
                </>
              )}
            </>
          </Stack>
        </ScrollView>

        {(criteriaName === 'bankName' || criteriaName === 'languageModal') && (
          <Stack className=" mt-4/2 w-full px-3">
            <Button
              size="lg"
              fullWidth
              onPress={() => {
                if (criteriaName === 'languageModal') {
                  store.setWebFlowLocale(changedLocale)
                  i18n.locale = changedLocale
                  changeLocaleInHeader(changedLocale)
                  queryClient.invalidateQueries({
                    queryKey: ['brandDetailsById'],
                  })
                }
                closeModal()
              }}
              disabled={!selectedValue}
            >
              {i18n.t('reconsent.continue')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default WebFlowSelectorModal
